import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
    Crown, Lock, ThumbsUp, ThumbsDown, Plus,
    CheckCircle, XCircle, Timer, Lightning
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

const CATEGORIES = ['general', 'marketplace', 'challenges', 'rewards', 'rides', 'seasonal', 'leaderboard'];

export default function Governance() {
    const { user } = useAuth();
    const [eligibility, setEligibility] = useState(null);
    const [proposals, setProposals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [newDesc, setNewDesc] = useState('');
    const [newCat, setNewCat] = useState('general');
    const [voting, setVoting] = useState(null);
    const [creating, setCreating] = useState(false);

    useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchData = async () => {
        try {
            const [eRes, pRes] = await Promise.all([
                fetch(`${API}/governance/eligibility`, { credentials: 'include', headers: getH() }),
                fetch(`${API}/governance/proposals`, { credentials: 'include', headers: getH() }),
            ]);
            if (eRes.ok) setEligibility(await eRes.json());
            if (pRes.ok) setProposals(await pRes.json());
        } catch (e) { console.error('Governance fetch error:', e); } finally { setLoading(false); }
    };

    const createProposal = async () => {
        if (!newTitle.trim() || !newDesc.trim()) { toast.error('Title and description required'); return; }
        setCreating(true);
        try {
            const res = await fetch(`${API}/governance/proposals`, {
                method: 'POST', headers: { ...getH(), 'Content-Type': 'application/json' },
                credentials: 'include', body: JSON.stringify({ title: newTitle, description: newDesc, category: newCat })
            });
            if (res.ok) {
                toast.success('Proposal created!');
                setShowCreate(false); setNewTitle(''); setNewDesc('');
                fetchData();
            } else { const e = await res.json(); toast.error(e.detail || 'Failed'); }
        } catch (e) { toast.error('Failed'); }
        finally { setCreating(false); }
    };

    const vote = async (proposalId, choice) => {
        setVoting(proposalId);
        try {
            const res = await fetch(`${API}/governance/proposals/${proposalId}/vote`, {
                method: 'POST', headers: { ...getH(), 'Content-Type': 'application/json' },
                credentials: 'include', body: JSON.stringify({ vote: choice })
            });
            if (res.ok) { const d = await res.json(); toast.success(`Vote recorded! Weight: ${d.weight}`); fetchData(); }
            else { const e = await res.json(); toast.error(e.detail || 'Vote failed'); }
        } catch (e) { toast.error('Vote failed'); }
        finally { setVoting(null); }
    };

    const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } catch (e) { return ''; } };

    return (
        <Layout>
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <span className="label-text text-boltz-gold text-glow-gold">VIP GOVERNANCE</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display">DAO Proposals</h1>
                    <p className="text-muted-foreground text-sm mt-1">Shape the future of Boltorium</p>
                </motion.div>

                {/* Eligibility Card */}
                {eligibility && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                        <Card className={`glass-card mb-6 ${eligibility.eligible ? 'border-boltz-gold/30 box-glow-gold' : 'border-white/5'}`}>
                            <CardContent className="p-5 flex items-center gap-4">
                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${eligibility.eligible ? 'bg-boltz-gold/20' : 'bg-white/5'}`}>
                                    {eligibility.eligible ? <Crown size={28} className="text-boltz-gold" weight="fill" /> : <Lock size={28} className="text-muted-foreground" />}
                                </div>
                                <div className="flex-1">
                                    <p className="font-bold text-sm">{eligibility.eligible ? 'VIP Access Granted' : 'VIP Access Locked'}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">{eligibility.reason}</p>
                                    <div className="flex gap-2 mt-2">
                                        <Badge className="bg-white/5 text-xs">{eligibility.volt_tier}</Badge>
                                        <Badge className="bg-boltz-gold/10 text-boltz-gold text-xs">{eligibility.staked_amount?.toFixed(0)} Boltz staked</Badge>
                                    </div>
                                </div>
                                {eligibility.eligible && (
                                    <Button size="sm" onClick={() => setShowCreate(true)} className="bg-boltz-gold text-black font-display" data-testid="create-proposal-btn">
                                        <Plus size={14} className="mr-1" />Propose
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* Create Proposal */}
                <AnimatePresence>
                    {showCreate && (
                        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                            <Card className="glass-card mb-6 border-boltz-gold/20">
                                <CardContent className="p-5 space-y-3">
                                    <Input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Proposal title" data-testid="proposal-title" />
                                    <textarea value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Describe your proposal..."
                                        className="w-full bg-card border border-border rounded-xl px-4 py-3 text-sm min-h-[100px] resize-none" data-testid="proposal-desc" />
                                    <select value={newCat} onChange={e => setNewCat(e.target.value)} className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm">
                                        {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                                    </select>
                                    <div className="flex gap-2">
                                        <Button onClick={createProposal} disabled={creating} className="flex-1 bg-boltz-gold text-black font-display" data-testid="submit-proposal">
                                            {creating ? 'Creating...' : 'Submit Proposal'}
                                        </Button>
                                        <Button onClick={() => setShowCreate(false)} variant="outline" className="border-white/10">Cancel</Button>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Proposals List */}
                {loading ? (
                    <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>
                ) : proposals.length === 0 ? (
                    <Card className="glass-card text-center py-16">
                        <Crown size={48} className="text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground font-display">No active proposals</p>
                        <p className="text-sm text-muted-foreground mt-1">VIP members can create proposals to shape Boltorium</p>
                    </Card>
                ) : (
                    <div className="space-y-3">
                        {proposals.map((p, i) => {
                            const total = p.votes_for + p.votes_against;
                            const forPct = total > 0 ? (p.votes_for / total) * 100 : 50;
                            return (
                                <motion.div key={p.proposal_id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                                    <Card className="glass-card">
                                        <CardContent className="p-5">
                                            <div className="flex items-start justify-between mb-2">
                                                <div>
                                                    <Badge className="bg-cyber-purple/10 text-cyber-purple text-[10px] border-cyber-purple/20 mb-1">{p.category}</Badge>
                                                    <h3 className="font-bold text-sm">{p.title}</h3>
                                                </div>
                                                <Badge variant="outline" className="text-[10px] border-white/10 shrink-0">
                                                    <Timer size={10} className="mr-1" />{fmtDate(p.expires_at)}
                                                </Badge>
                                            </div>
                                            <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{p.description}</p>
                                            <p className="text-[10px] text-muted-foreground mb-2">by {p.author_name} ({p.author_tier})</p>

                                            {/* Vote bar */}
                                            <div className="mb-3">
                                                <div className="flex justify-between text-[10px] mb-1">
                                                    <span className="text-neon-green">For: {p.votes_for}</span>
                                                    <span className="text-muted-foreground">{total} votes</span>
                                                    <span className="text-plasma-red">Against: {p.votes_against}</span>
                                                </div>
                                                <div className="h-2 rounded-full bg-white/5 overflow-hidden flex">
                                                    <div className="bg-neon-green h-full transition-all" style={{ width: `${forPct}%` }} />
                                                    <div className="bg-plasma-red h-full transition-all" style={{ width: `${100 - forPct}%` }} />
                                                </div>
                                            </div>

                                            {/* Vote buttons */}
                                            {p.user_vote ? (
                                                <Badge className={`${p.user_vote === 'for' ? 'bg-neon-green/10 text-neon-green' : 'bg-plasma-red/10 text-plasma-red'} text-xs`}>
                                                    {p.user_vote === 'for' ? <CheckCircle size={12} className="mr-1" /> : <XCircle size={12} className="mr-1" />}
                                                    Voted {p.user_vote}
                                                </Badge>
                                            ) : eligibility?.eligible ? (
                                                <div className="flex gap-2">
                                                    <Button size="sm" onClick={() => vote(p.proposal_id, 'for')} disabled={voting === p.proposal_id}
                                                        className="flex-1 bg-neon-green/10 text-neon-green border border-neon-green/20 text-xs hover:bg-neon-green/20" data-testid={`vote-for-${p.proposal_id}`}>
                                                        <ThumbsUp size={14} className="mr-1" />For
                                                    </Button>
                                                    <Button size="sm" onClick={() => vote(p.proposal_id, 'against')} disabled={voting === p.proposal_id}
                                                        className="flex-1 bg-plasma-red/10 text-plasma-red border border-plasma-red/20 text-xs hover:bg-plasma-red/20" data-testid={`vote-against-${p.proposal_id}`}>
                                                        <ThumbsDown size={14} className="mr-1" />Against
                                                    </Button>
                                                </div>
                                            ) : (
                                                <p className="text-[10px] text-muted-foreground text-center">VIP access required to vote</p>
                                            )}
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </div>
        </Layout>
    );
}
